// Bridges the React editor with the prototype's 3D renderer (public/planner-3d.js) and builds
// small drawings (module front thumbnails) from the shared core.
import { bodyMaterialOf, type CameraState, type Drawing, type DrawItem, drawsNative, extOf, front2D, frontsOf, geo, isLooseFrontBoard, type ModuleInstance, moduleBoards, type ProjectData, standOf, zocaloCm, zr } from '@core';
import type { CatalogMaterial } from '../api';

/** three.js files vendored in public/vendor (the renderer loads unpkg otherwise). */
const THREE_FILES = [
  'build/three.module.js',
  'build/three.core.js',
  'examples/jsm/controls/OrbitControls.js',
  'examples/jsm/environments/RoomEnvironment.js',
  'examples/jsm/loaders/GLTFLoader.js',
  'examples/jsm/geometries/RoundedBoxGeometry.js',
  'examples/jsm/utils/BufferGeometryUtils.js',
  'examples/jsm/utils/SkeletonUtils.js',
  // Ambient occlusion (GTAO) for the gallery / PDF renders.
  'examples/jsm/postprocessing/EffectComposer.js',
  'examples/jsm/postprocessing/RenderPass.js',
  'examples/jsm/postprocessing/GTAOPass.js',
  'examples/jsm/postprocessing/OutputPass.js',
  'examples/jsm/postprocessing/ShaderPass.js',
  'examples/jsm/postprocessing/MaskPass.js',
  'examples/jsm/postprocessing/Pass.js',
  'examples/jsm/shaders/CopyShader.js',
  'examples/jsm/shaders/GTAOShader.js',
  'examples/jsm/shaders/PoissonDenoiseShader.js',
  'examples/jsm/shaders/OutputShader.js',
  'examples/jsm/math/SimplexNoise.js',
];

interface ProtoMat {
  name: string;
  type: string;
  c: string;
  wood?: number;
  img?: string;
  tile?: number;
  rough?: number;
  user?: number;
}

declare global {
  interface Window {
    SPEngine?: { MATS: Record<string, ProtoMat>; geo: (m: Parameters<typeof geo>[0]) => ReturnType<typeof geo>; zr: typeof zr; room: { A: number; B: number } };
    __resources?: Record<string, string>;
  }
}

/** Keeps window.SPEngine.MATS in sync with the organisation's catalogue (the renderer reads it). */
export function installEngine(materials: CatalogMaterial[]) {
  const MATS: Record<string, ProtoMat> = {};
  for (const m of materials) {
    MATS[m.code] = {
      name: m.name,
      type: m.type,
      c: m.color,
      wood: m.grain !== 'ninguna' ? 1 : undefined,
      ...(m.source === 'subido' && m.maps.baseColor ? { img: m.maps.baseColor.url, tile: m.sizeWcm ?? 60, rough: m.roughness ?? 0.5, user: 1 } : {}),
    };
  }
  if (!MATS.blanco) MATS.blanco = { name: 'Blanco', type: 'Melamina', c: '#eeebe6' };
  const room = window.SPEngine?.room ?? { A: 360, B: 300 };
  // geo() needs the room size for walls C and D; sceneCfg() keeps it current.
  window.SPEngine = { MATS, geo: (m) => geo(m, window.SPEngine?.room), zr, room };
  window.__resources = Object.fromEntries(THREE_FILES.map((p) => [`th_${p.replace(/[^a-z0-9]/gi, '_')}`, `/vendor/three/${p}`]));
}

export interface Viewer {
  update(cfg: unknown): Promise<void>;
  dispose(): void;
  zoomBy(f: number): void;
  /** az: radians, or 'auto' for the angle that faces the most fronts. */
  fit(instant?: boolean, az?: number | 'auto'): void;
  setAngle(deg: number): void;
  /** Opens (true) or closes all doors and drawers, animated. */
  setOpen(open: boolean): void;
  /** Cast shadows on/off (the editor's "Sombras" button; renders for the gallery and PDF always keep them). */
  setShadows(on: boolean): void;
  getCamera(): CameraState;
  focus(id: number): void;
  setCamera(c: CameraState): void;
  /** Camera outside a wall looking in: the wall hides and the backs of the units and the installation show. */
  viewBehind(wall: 'A' | 'B' | 'C' | 'D'): void;
  /** Move mode: pressing on a module drags it (the camera stays still). */
  setMoveMode(on: boolean): void;
  /** Shows a module at a new place while it is dragged (the editor commits it at the end). */
  previewMove(id: number, patch: { wall?: string; pos?: number; x?: number; y?: number; rot?: number }): void;
}
/** Drag of a module in the 3D view: offset in cm over the floor (x along wall A, z along wall B). */
export type Drag3D = (id: number, dx: number, dz: number, phase: 'start' | 'move' | 'end' | 'cancel') => void;
interface Renderer {
  init(): Promise<void>;
  createViewer(host: HTMLElement, opts: { onSelect?: (id: number | null, add: boolean) => void; onDrag?: Drag3D; onReady?: () => void }): Promise<Viewer>;
  snapshot(cfg: unknown, o: SnapOptions): Promise<string | null>;
  /** COLLADA (.dae) of the furniture for SketchUp. */
  exportDae(cfg: unknown): Promise<string>;
}

export interface SnapOptions {
  w: number;
  h: number;
  /** Camera azimuth in degrees (45 = corner view). */
  ang?: number;
  /** Frame one module (detail view). */
  focusId?: number;
  /** Render with doors and drawers open. */
  open?: boolean;
  /** Hand-made camera; overrides ang / focusId framing. */
  cam?: CameraState;
  /** false: plain render without ambient occlusion (faster). */
  hq?: boolean;
}

const snapCache = new Map<string, Promise<string | null>>();
/** Offscreen photo-real render (JPEG data URL), cached by scene + options; null when WebGL is unavailable. */
export function snapshot(cfg: unknown, o: SnapOptions): Promise<string | null> {
  const key = JSON.stringify([cfg, o]);
  let p = snapCache.get(key);
  if (!p) {
    p = loadRenderer()
      .then((r) => r.snapshot(cfg, o))
      .catch(() => null);
    snapCache.set(key, p);
    if (snapCache.size > 40) snapCache.delete(snapCache.keys().next().value!);
  }
  return p;
}

let rendererP: Promise<Renderer> | null = null;
export function loadRenderer(): Promise<Renderer> {
  rendererP ??= import(/* @vite-ignore */ new URL(__RENDERER_URL__, window.location.origin).href) as Promise<Renderer>;
  return rendererP;
}

export const handleOf = (a?: string) => (a === 'Gola' ? 'gola' : a === 'Push' ? 'none' : 'bar');

/** Scene config the renderer expects (prototype sceneCfg()). */
export function sceneCfg(p: ProjectData, extra: { sel: number | null; sels?: number[]; cotas: boolean; altos: boolean; dark: boolean }) {
  if (window.SPEngine) window.SPEngine.room = { A: p.room.A, B: p.room.B };
  return {
    // Uploaded modules drawn as native modules (doors and drawers read from their boards or set in the library):
    // plinth, materials, handles and opening like the catalogue ones. The rest keep their own 3D model.
    mods: p.mods.map((m) =>
      m.glb && drawsNative(m)
        ? { ...m, glb: undefined, fr: frontsOf(m) }
        : // A loose board (TV panel, floating shelf) is cut from the fronts' board: draw it in that finish too.
          isLooseFrontBoard(m) && !m.cue
          ? { ...m, cue: bodyMaterialOf(m, p.mats) }
          : // Built panel by panel in the designer: the 3D draws the same boards as the despiece (fronts apart).
            m.build && !m.glb
            ? withExt(m, p)
            : m,
    ),
    mats: p.mats,
    room: p.room,
    ops: p.ops,
    handle: handleOf(p.prefs.apertura),
    zoc: zocaloCm(p.prefs.zocalo),
    junta: p.prefs.junta ?? 4,
    pts: p.pts,
    walls: p.walls,
    panels: p.panels,
    kitchen: p.ptype === 'cocina',
    sel: extra.sel,
    sels: extra.sels ?? [],
    cotas: extra.cotas,
    altos: extra.altos,
    bg: extra.dark ? '#131211' : '#d3cec6',
  };
}

/** A module built panel by panel: its boards for the 3D, what it stands on, and where its fronts fit (exterior zones). */
function withExt(m: ModuleInstance, p: ProjectData) {
  const stand = standOf(m, zocaloCm(p.prefs.zocalo));
  const x = extOf(m);
  const keep = new Set(['frontal', 'ext-izq', 'ext-der', 'cornisa']);
  return {
    ...m,
    stand: stand.kind,
    fx0: x.left,
    fx1: x.right,
    ftop: x.top,
    carcass: moduleBoards(m, 18, 18, p.prefs.junta ?? 4, stand.h * 10).filter((b) => b.slot !== 'frentes' || keep.has(b.role)),
  };
}

type Colors = { f: string; b: string; c: string; hd: string };
export function colorsFor(m: Pick<ModuleInstance, 'cue' | 'fre'> & Partial<ModuleInstance>, mats: ProjectData['mats'], byCode: Record<string, { color: string }>): Colors {
  const col = (code: string, fb: string) => byCode[code]?.color ?? fb;
  return { f: col(m.fre || mats.frentes, '#c49a6c'), b: col(bodyMaterialOf(m, mats), '#eeebe6'), c: col(mats.encimera, '#e9e7e2'), hd: col(mats.jaladeras, '#2a2928') };
}

/** Prototype frontThumb(): the module's front elevation with some padding. */
export function frontThumb(m: ModuleInstance, mats: ProjectData['mats'], byCode: Record<string, { color: string }>, apertura?: string): Drawing {
  const items: DrawItem[] = [];
  front2D({ ...m, fr: frontsOf(m) }, 0, 0, m.w, m.h, colorsFor(m, mats, byCode), handleOf(apertura), items);
  const pad = Math.max(m.w, m.h) * 0.08;
  return { items, vb: [-pad, -pad, m.w + pad * 2, m.h + pad * 2] };
}

// "Sombras" preference of this device (a slow phone or laptop can turn them off); every live 3D viewer follows it.
const SHADOWS_KEY = 'planner.sombras';
export function shadowsOn(): boolean {
  try {
    return localStorage.getItem(SHADOWS_KEY) !== '0';
  } catch {
    return true;
  }
}
export function setShadowsPref(on: boolean) {
  try {
    localStorage.setItem(SHADOWS_KEY, on ? '1' : '0');
  } catch {}
  window.dispatchEvent(new CustomEvent('planner:sombras', { detail: on }));
}

/** The project's furniture as a COLLADA file (SketchUp: Archivo → Importar), named after the project. */
export async function exportSketchup(p: ProjectData): Promise<string> {
  const r = await loadRenderer();
  const dae = await r.exportDae({ ...sceneCfg(p, { sel: null, cotas: false, altos: true, dark: false }), name: p.pname });
  const name = `${(p.pname || 'proyecto').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w-]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'proyecto'}.dae`;
  const url = URL.createObjectURL(new Blob([dae], { type: 'model/vnd.collada+xml' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return name;
}
